import React, { useEffect, useState } from "react";
import "../../style/admin/UserManagement.css";
import { fetchApi } from "../../utils/api";
import UserInfo from "./UserInfo";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [showEditCreditModal, setShowEditCreditModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [updatingCredit, setUpdatingCredit] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await fetchApi("/auth/admin/users");
      setUsers(data);
    } catch (err) {
      setError(err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (id) => {
    try {
      const user = await fetchApi(`/auth/admin/users/${id}`);
      setSelectedUser(user);
      setShowModal(true);
    } catch (err) {
      alert("Failed to fetch user detail: " + err.message);
    }
  };

  const handleRoleChange = async (id, newRole) => {
    if (!window.confirm(`Change this user's role to "${newRole}"?`)) return;

    try {
      const response = await fetchApi(`/auth/admin/update-role`, {
        method: "POST",
        body: { user_id: id, role: newRole },
      });

      if (response.success) {
        alert("User role updated successfully!");
        setUsers((prev) =>
          prev.map((u) =>
            u.id === id ? { ...u, role: newRole } : u
          )
        );
      } else {
        alert(response.error || "Failed to update user role");
      }
    } catch (err) {
      alert("An error occurred: " + err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handleEdit = (id) => {
    const user = users.find((u) => u.id === id);
    setResetUser(user);
    setShowResetModal(true);
  };

  const confirmResetPassword = async () => {
    if (!resetUser) return;
    try {
      await fetchApi(`/auth/admin/reset-password`, {
        method: "POST",
        body: { user_id: resetUser.id },
      });
      alert(`${resetUser.name}'s password has been reset. New password sent to email.`);
      setShowResetModal(false);
      setResetUser(null);
    } catch (err) {
      alert("Failed to reset password: " + err.message);
    }
  };

  // Enhanced delete function with force delete option
  const handleDelete = async (id) => {
    const user = users.find(u => u.id === id);
    
    // First confirmation with warning
    if (!window.confirm(
      "WARNING: You are about to permanently delete this user!\n\n" +
      `User: ${user?.name || 'Unknown'} (${user?.email || 'No email'})\n\n` +
      "This will delete:\n" +
      "• All user data\n" +
      "• Deposit history\n" +
      "• Commission history\n" +
      "• Referral data\n" +
      "• Support tickets\n" +
      "• User activities\n\n" +
      "THIS ACTION CANNOT BE UNDONE!\n\n" +
      "Are you sure you want to continue?"
    )) return;

    // Ask for force delete option
    const useForceDelete = window.confirm(
      "Use FORCE DELETE?\n\n" +
      "• YES: Will forcefully delete even if there are references\n" +
      "• NO: Will try normal deletion first\n\n" +
      "Choose 'OK' for force delete, 'Cancel' for normal delete"
    );

    setDeletingUserId(id);
    
    try {
      let response;
      
      if (useForceDelete) {
        // Force delete endpoint
        response = await fetchApi(`/auth/admin/users/${id}/force`, { 
          method: "DELETE" 
        });
      } else {
        // Normal delete with force option in body
        response = await fetchApi(`/auth/admin/users/${id}`, { 
          method: "DELETE",
          body: { force: false }
        });
      }

      // Update state
      setUsers((prev) => prev.filter((u) => u.id !== id));
      
      // Show success message
      alert(
        response.message || 
        `User ${user?.name || ''} deleted successfully${useForceDelete ? ' (force delete)' : ''}`
      );
      
    } catch (err) {
      console.error("Delete error:", err);
      
      // Handle specific error codes
      if (err.message.includes("409") || err.message.includes("Conflict")) {
        // Parse error message if available
        let errorDetail = err.message;
        try {
          const errorData = JSON.parse(err.message);
          errorDetail = errorData.error || errorData.message || err.message;
        } catch (e) {
          // Use original message
        }
        
        const retryForceDelete = window.confirm(
          `Failed to delete user: Still has related data.\n\n` +
          `Error: ${errorDetail}\n\n` +
          `Do you want to try FORCE DELETE?\n\n` +
          `Warning: This will attempt to forcefully delete all related data.`
        );
        
        if (retryForceDelete) {
          // Try force delete
          await handleForceDelete(id, user);
        } else {
          alert("Deletion cancelled.");
        }
      } else {
        alert("Failed to delete user: " + err.message);
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  // Force delete function
  const handleForceDelete = async (id, user) => {
    const confirmForce = window.confirm(
      "FORCE DELETE CONFIRMATION\n\n" +
      `This will permanently delete user: ${user?.name || 'Unknown'}\n\n` +
      "All related data will be forcibly removed regardless of constraints.\n" +
      "This may affect database integrity if not handled properly.\n\n" +
      "Are you absolutely sure you want to proceed?"
    );
    
    if (!confirmForce) return;
    
    setDeletingUserId(id);
    
    try {
      const response = await fetchApi(`/auth/admin/users/${id}/force`, { 
        method: "DELETE" 
      });
      
      setUsers((prev) => prev.filter((u) => u.id !== id));
      
      alert(
        response.message || 
        `User ${user?.name || ''} successfully deleted with FORCE DELETE!`
      );
      
    } catch (err) {
      console.error("Force delete error:", err);
      
      // Last resort suggestion
      alert(
        `Force delete failed: ${err.message}\n\n` +
        `Please check database manually or contact database administrator.`
      );
    } finally {
      setDeletingUserId(null);
    }
  };

  // Approve user
// Perbarui fungsi handleApprove di UserManagement.jsx

const handleApprove = async (id) => {
  try {
    // Tampilkan loading state jika ada
    // setLoading(true);
    
    const res = await fetchApi(`/auth/admin/verify-user`, {
      method: "POST",
      body: { user_id: id, action: "approve" },
    });

    // Log response untuk debugging
    console.log('Approve response:', res);

    if (res.success) {
      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, status: "Register Success", is_active: 1 } : u
        )
      );
      
      // Tampilkan pesan sukses
      alert(res.message || "User approved successfully");
      
      // Optional: refresh data dari server
      // await loadUsers();
    } else {
      // Tampilkan error dari server
      alert(res.error || "Failed to approve user");
    }
  } catch (err) {
    console.error("Approve error:", err);
    
    // Cek apakah error karena email atau karena hal lain
    if (err.message.includes("email") || err.message.includes("mail")) {
      // Jika error email, user mungkin sudah terapprove di database
      alert("User approved but email notification failed to send. Please check email configuration.");
      
      // Tetap update UI
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, status: "Register Success" } : u
        )
      );
    } else {
      alert("Failed to approve user: " + err.message);
    }
  } finally {
    // setLoading(false);
  }
};

  // Block user
  const handleBlock = async (id) => {
    const reason = prompt("Enter block reason:", "Blocked by admin");
    if (!reason) return;
    
    try {
      const res = await fetchApi(`/auth/admin/verify-user`, {
        method: "POST",
        body: { user_id: id, action: "block", reason },
      });

      if (res.success) {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === id ? { ...u, status: "Register Failed" } : u
          )
        );
        alert(res.message || "User blocked successfully");
      } else {
        alert(res.error || "Failed to block user");
      }
    } catch (err) {
      alert("Failed to block user: " + err.message);
    }
  };

  // Handle edit credit
  const handleEditCredit = (user) => {
    setSelectedUser(user);
    setCreditAmount(user.total_credit || "0");
    setShowEditCreditModal(true);
  };

  // Confirm update credit
  const confirmUpdateCredit = async () => {
    if (!selectedUser || !creditAmount) return;
    
    setUpdatingCredit(true);
    try {
      const response = await fetchApi(`/auth/admin/update-credit`, {
        method: "POST",
        body: { 
          user_id: selectedUser.id, 
          credit_amount: parseFloat(creditAmount) 
        },
      });
      
      if (response.success) {
        alert(`Credit for ${selectedUser.name} has been updated successfully.`);
        
        // Refresh users data
        await loadUsers();
        
        setShowEditCreditModal(false);
        setSelectedUser(null);
        setCreditAmount("");
      } else {
        alert(response.error || "Failed to update credit");
      }
    } catch (err) {
      console.error("Update credit error:", err);
      if (err.message.includes("Failed to fetch") || err.message.includes("connection refused")) {
        alert("Cannot connect to server. Please check if backend is running on port 5000.");
      } else {
        alert("Failed to update credit: " + err.message);
      }
    } finally {
      setUpdatingCredit(false);
    }
  };

  // Soft delete function (alternative)
  const handleSoftDelete = async (id) => {
    const user = users.find(u => u.id === id);
    
    if (!window.confirm(
      `Mark user ${user?.name || ''} as deleted?\n\n` +
      `Data will be preserved in database but hidden from view.\n` +
      `This action can be reversed by database administrator.\n\n` +
      `Continue?`
    )) return;
    
    try {
      const response = await fetchApi(`/auth/admin/users/${id}/soft-delete`, { 
        method: "POST" 
      });
      
      // Remove from view
      setUsers((prev) => prev.filter((u) => u.id !== id));
      
      alert(
        response.message || 
        `User ${user?.name || ''} has been marked as deleted (soft delete)`
      );
      
    } catch (err) {
      alert("Soft delete failed: " + err.message);
    }
  };

  // === Filtering logic ===
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.phone || "").toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "" || statusFilter === "Status" || u.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // === Pagination logic ===
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container">
      <div className="main-content-manage">
        <h1>User Management</h1>
        <div className="card-manage">
          <h3>Registered Users</h3>

          {loading ? (
            <div className="loading-state">
              <p>Loading users...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <h4>Error</h4>
              <p>{error}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <img
                src="https://cdn-icons-png.flaticon.com/512/4076/4076505.png"
                alt="empty"
              />
              <h4>No Registered Users Yet</h4>
              <p>There are no users registered yet.</p>
            </div>
          ) : (
            <>
              {/* Filter section */}
              <div className="search-filter">
                <input
                  type="text"
                  placeholder="Search user..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="Register Success">Register Success</option>
                  <option value="Register Failed">Register Failed</option>
                  <option value="Checking Register">Checking Register</option>
                </select>
              </div>

              {/* Table */}
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>WhatsApp Number</th>
                      <th>Referral Code</th>
                      <th>Credit</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Register Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.map((u, i) => (
                      <tr key={u.id}>
                        <td>{indexOfFirstUser + i + 1}</td>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td>{u.phone || "-"}</td>
                        <td>{u.referral || "-"}</td>
                        <td>{u.total_credit || "0.00"}</td>
                        <td>
                          <select
                            value={u.role || "user"}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            className="role-dropdown"
                            disabled={deletingUserId === u.id}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td>
                          <span className={`status ${u.status?.toLowerCase().replace(' ', '-')}`}>
                            {u.status}
                          </span>
                        </td>
                        <td>
                          {u.date ? new Date(u.date).toLocaleDateString() : "-"}
                        </td>
                        <td className="actions">
                          <button 
                            className="view-user" 
                            onClick={() => handleView(u.id)} 
                            title="View Details"
                            disabled={deletingUserId === u.id}
                          >👁️</button>
                          
                          <button 
                            className="edit-credit" 
                            onClick={() => handleEditCredit(u)}
                            title="Edit Credit"
                            disabled={deletingUserId === u.id}
                          >💰</button>

                          {u.status === "Register Success" && (
                            <>
                              <button 
                                className="edit-user" 
                                onClick={() => handleEdit(u.id)} 
                                title="Reset Password"
                                disabled={deletingUserId === u.id}
                              >✏️</button>
                              <button 
                                className="block-user" 
                                onClick={() => handleBlock(u.id)} 
                                title="Block User"
                                disabled={deletingUserId === u.id}
                              >🚫</button>
                              <button 
                                className="delete-user" 
                                onClick={() => handleDelete(u.id)} 
                                title="Delete User Permanently"
                                disabled={deletingUserId === u.id}
                              >
                                {deletingUserId === u.id ? "⏳" : "❌"}
                              </button>
                            </>
                          )}

                          {u.status === "Register Failed" && (
                            <>
                              <button 
                                className="block-user" 
                                onClick={() => handleBlock(u.id)} 
                                title="Block User"
                                disabled={deletingUserId === u.id}
                              >🚫</button>
                              <button 
                                className="delete-user" 
                                onClick={() => handleDelete(u.id)} 
                                title="Delete User Permanently"
                                disabled={deletingUserId === u.id}
                              >
                                {deletingUserId === u.id ? "⏳" : "❌"}
                              </button>
                            </>
                          )}

                          {u.status === "Checking Register" && (
                            <>
                              <button 
                                className="block-user" 
                                onClick={() => handleBlock(u.id)} 
                                title="Block User"
                                disabled={deletingUserId === u.id}
                              >🚫</button>
                              <button 
                                className="delete-user" 
                                onClick={() => handleDelete(u.id)} 
                                title="Delete User Permanently"
                                disabled={deletingUserId === u.id}
                              >
                                {deletingUserId === u.id ? "⏳" : "❌"}
                              </button>
                              <button 
                                className="approve" 
                                onClick={() => handleApprove(u.id)} 
                                title="Approve User"
                                disabled={deletingUserId === u.id}
                              >✔️</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-user-manage">
                  <button
                    onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    {"<"}
                  </button>

                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      className={currentPage === idx + 1 ? "active" : ""}
                      onClick={() => paginate(idx + 1)}
                    >
                      {idx + 1}
                    </button>
                  ))}

                  <button
                    onClick={() =>
                      currentPage < totalPages && paginate(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                  >
                    {">"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Credit Modal */}
      {showEditCreditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Credit</h3>
            <p>
              Edit credit amount for user <b>{selectedUser?.name}</b>
            </p>
            
            <div className="modal-input">
              <label>Credit Amount:</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="Enter credit amount"
                disabled={updatingCredit}
              />
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => setShowEditCreditModal(false)}
                disabled={updatingCredit}
              >
                Cancel
              </button>
              <button 
                onClick={confirmUpdateCredit}
                disabled={updatingCredit}
              >
                {updatingCredit ? "Updating..." : "Update Credit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Info Modal */}
      {showModal && (
        <UserInfo
          user={selectedUser}
          onClose={handleCloseModal}
          refreshUsers={loadUsers}
        />
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reset Password</h3>
            <p>
              You are about to reset password for user <b>{resetUser?.name}</b>. 
              A new password will be sent to the user's email.
            </p>
            <div className="modal-actions">
              <button onClick={() => setShowResetModal(false)}>Cancel</button>
              <button onClick={confirmResetPassword}>Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;